import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import "./FullBlog.css";
import NavBar from "../Navigation/NavBar";
import Footer from "../Footer/Footer";
import { Icon } from "@iconify/react";
import arrowLeft from "@iconify/icons-mdi/arrow-left";
import calendarIcon from "@iconify/icons-mdi/calendar-outline";
import clockIcon from "@iconify/icons-mdi/clock-time-four-outline";
import linkIcon from "@iconify/icons-mdi/link-variant";
import linkedinIcon from "@iconify/icons-mdi/linkedin";
import twitterIcon from "@iconify/icons-mdi/twitter";
import arrowRight from "@iconify/icons-mdi/arrow-right";
import { Helmet } from "react-helmet-async";
import axios from "axios";

function FullBlog() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const [blogData, setBlogData] = useState(state?.props || null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(!state?.props);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const GoBack = () => {
    navigate("/Blog");
  };

  const rawApi = process.env.REACT_APP_API_URL || (process.env.NUXT_PUBLIC_API_URL ? `${process.env.NUXT_PUBLIC_API_URL}/api` : 'https://api.khushbufoundation.com/api');
  const backendUrl = rawApi.endsWith('/api') ? rawApi : `${rawApi}/api`;
  const backendOrigin = backendUrl.replace(/\/api$/, '');

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    // Fetch related/recent blogs for the sidebar & footer recommendations
    axios
      .get(`${backendUrl}/quarec/blogs`)
      .then((res) => {
        const blogs = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setRecentBlogs(blogs.slice(0, 4));
      })
      .catch((err) => {
        console.warn("Could not fetch recent Quarec blogs:", err);
      });

    // If blog data wasn't passed via route state and ID exists, fetch it
    if (!state?.props && id) {
      axios
        .get(`${backendUrl}/quarec/blogs/slug/${id}`)
        .then((res) => {
          setBlogData(res.data);
          setLoading(false);
        })
        .catch(() => {
          // Try fetching by mongo ID
          axios
            .get(`${backendUrl}/quarec/blogs/${id}`)
            .then((res2) => {
              setBlogData(res2.data);
              setLoading(false);
            })
            .catch(() => {
              setError("Article not found or could not be loaded.");
              setLoading(false);
            });
        });
    }
  }, [id, state, backendUrl]);

  const resolveImage = (imgUrl) => {
    if (!imgUrl) return "/favicon.ico";
    if (imgUrl.startsWith("http://") || imgUrl.startsWith("https://")) return imgUrl;
    return `${backendOrigin}${imgUrl.startsWith("/") ? "" : "/"}${imgUrl}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Recently Published";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  // Estimate read time based on content length
  const readTimeMinutes = () => {
    const text = (blogData?.content || "").replace(/<[^>]*>/g, ' ');
    const words = text.trim().split(/\s+/).length;
    return Math.max(2, Math.ceil(words / 200));
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank");
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(blogData?.title || "Insights from Quarec Resources");
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, "_blank");
  };

  return (
    <>
      <NavBar />
      <div className="qr-detail-page">
        {/* Top Navigation Bar with Back Button & Breadcrumbs */}
        <section className="qr-detail-nav">
          <div className="qr-nav-container">
            <button onClick={GoBack} className="qr-back-btn">
              <Icon icon={arrowLeft} style={{ fontSize: "18px" }} />
              <span>Back to Articles</span>
            </button>
            <div className="qr-breadcrumbs">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/Blog">Blog</Link>
              <span>/</span>
              <span style={{ color: "#0f172a", fontWeight: "600" }}>Article</span>
            </div>
          </div>
        </section>

        {loading ? (
          <div className="qr-state-box">
            <div className="qr-state-spinner"></div>
            <h2 className="qr-state-title">Loading Article Content...</h2>
            <p className="qr-state-text">Fetching live insights from Quarec's knowledge hub.</p>
          </div>
        ) : error || !blogData ? (
          <div className="qr-state-box">
            <h2 className="qr-state-title">{error || "Article could not be found."}</h2>
            <p className="qr-state-text">The requested publication might have been updated or moved.</p>
            <button onClick={GoBack} className="qr-back-btn" style={{ margin: "0 auto" }}>
              Explore Other Articles
            </button>
          </div>
        ) : (
          <div className="qr-article-master-wrap">
            <Helmet>
              <title>{blogData.metaTitle || blogData.title} | Quarec Resources</title>
              <meta
                name="description"
                content={blogData.metaDescription || blogData.content?.replace(/<[^>]*>/g, '').slice(0, 160)}
              />
              <link rel="canonical" href={`https://quarec.com${window.location.pathname}`} />
              <meta property="og:type" content="article" />
              <meta property="og:url" content={`https://quarec.com${window.location.pathname}`} />
              <meta property="og:title" content={blogData.metaTitle || blogData.title} />
              <meta property="og:description" content={blogData.metaDescription || blogData.content?.replace(/<[^>]*>/g, '').slice(0, 160)} />
              {blogData.image && <meta property="og:image" content={resolveImage(blogData.image)} />}
              <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                headline: blogData.title,
                mainEntityOfPage: `https://quarec.com${window.location.pathname}`,
                author: { "@type": "Organization", name: blogData.author || "Quarec Resources" },
                publisher: { "@type": "Organization", name: "Quarec Resources", url: "https://quarec.com" },
                ...(blogData.createdAt ? {datePublished: blogData.createdAt} : {}),
                ...(blogData.updatedAt ? {dateModified: blogData.updatedAt} : {}),
                ...(blogData.image ? {image: resolveImage(blogData.image)} : {})
              })}</script>
            </Helmet>

            {/* Article Headline & Byline Header */}
            <header className="qr-article-header">
              <span className="qr-article-category-pill">
                {blogData.category || "Recruitment Strategy"}
              </span>

              <h1 className="qr-article-headline">{blogData.title}</h1>

              <div className="qr-article-byline">
                <div className="qr-author-meta">
                  <div className="qr-author-avatar-lg">
                    {blogData.author ? blogData.author.charAt(0) : "Q"}
                  </div>
                  <div className="qr-author-title-group">
                    <div className="name">{blogData.author || "Quarec Editorial Board"}</div>
                    <div className="sub">Executive Talent Advisory</div>
                  </div>
                </div>

                <div className="qr-date-reading">
                  <span className="qr-meta-item">
                    <Icon icon={calendarIcon} style={{ fontSize: "16px" }} />
                    <span>{formatDate(blogData.createdAt || blogData.updatedAt)}</span>
                  </span>
                  <span>&bull;</span>
                  <span className="qr-meta-item">
                    <Icon icon={clockIcon} style={{ fontSize: "16px" }} />
                    <span>{readTimeMinutes()} min read</span>
                  </span>
                </div>
              </div>
            </header>

            {/* 16:9 Standard Featured Cover Banner */}
            {blogData.image && (
              <div className="qr-article-cover-wrapper">
                <div className="qr-cover-aspect-box">
                  <img
                    className="qr-article-cover"
                    src={resolveImage(blogData.image)}
                    alt={blogData.title}
                  />
                  <span className="qr-cover-ratio-badge">16:9 Standard</span>
                </div>
              </div>
            )}

            {/* Main Layout: Article Body + Sticky Advisory Sidebar */}
            <div className="qr-editorial-columns-layout">
              {/* Primary Content Column */}
              <main className="qr-article-main-col">
                <article className="qr-article-content-card">
                  <div
                    className="qr-prose"
                    dangerouslySetInnerHTML={{
                      __html: blogData.content || blogData.description || "",
                    }}
                  />
                </article>

                {/* Article Share Bar */}
                <div className="qr-article-footer-box">
                  <div className="qr-share-section">
                    <span className="qr-share-label">Share Article:</span>
                    <button
                      className="qr-share-btn"
                      onClick={handleCopyLink}
                      title={copied ? "Link Copied!" : "Copy Link"}
                    >
                      <Icon icon={linkIcon} />
                    </button>
                    <button
                      className="qr-share-btn"
                      onClick={shareToLinkedIn}
                      title="Share on LinkedIn"
                    >
                      <Icon icon={linkedinIcon} />
                    </button>
                    <button
                      className="qr-share-btn"
                      onClick={shareToTwitter}
                      title="Share on X"
                    >
                      <Icon icon={twitterIcon} />
                    </button>
                    {copied && (
                      <span className="qr-copy-toast-badge">
                        Copied to clipboard!
                      </span>
                    )}
                  </div>

                  <button onClick={GoBack} className="qr-back-btn">
                    <Icon icon={arrowLeft} />
                    <span>Return to Blog Hub</span>
                  </button>
                </div>

                {/* Author Bio Box */}
                <div className="qr-author-bio-card">
                  <div className="qr-author-bio-avatar">
                    {blogData.author ? blogData.author.charAt(0) : "Q"}
                  </div>
                  <div>
                    <h4 className="qr-author-bio-name">{blogData.author || "Quarec Editorial Team"}</h4>
                    <p className="qr-author-bio-desc">
                      Specialized talent acquisition advisors and executive headhunters delivering world-class recruitment solutions, permanent staffing, and payroll management across India and global markets.
                    </p>
                  </div>
                </div>
              </main>

              {/* Sticky Sidebar Column */}
              <aside className="qr-article-sidebar-col">
                {/* Consultation Card */}
                <div className="qr-sidebar-cta-card">
                  <span className="qr-sidebar-pill">{"// EXECUTIVE HIRING"}</span>
                  <h3 className="qr-sidebar-cta-title">Scale Your Workforce</h3>
                  <p className="qr-sidebar-cta-desc">
                    Connect with Quarec's senior recruitment consultants for niche tech talent, leadership hiring, and rapid staffing.
                  </p>
                  <button
                    onClick={() => navigate("/ContactUs")}
                    className="qr-sidebar-cta-btn"
                  >
                    <span>Request Talent Advisory</span>
                    <Icon icon={arrowRight} />
                  </button>
                </div>

                {/* Article Info Summary */}
                <div className="qr-sidebar-meta-card">
                  <h4 className="qr-sidebar-heading">{"// ARTICLE METRICS"}</h4>
                  <div className="qr-sidebar-meta-row">
                    <span className="label">Category:</span>
                    <span className="value">{blogData.category || "Staffing"}</span>
                  </div>
                  <div className="qr-sidebar-meta-row">
                    <span className="label">Read Time:</span>
                    <span className="value">{readTimeMinutes()} min read</span>
                  </div>
                  <div className="qr-sidebar-meta-row">
                    <span className="label">Published:</span>
                    <span className="value">{formatDate(blogData.createdAt || blogData.updatedAt)}</span>
                  </div>
                  <div className="qr-sidebar-meta-row">
                    <span className="label">Ratio:</span>
                    <span className="value">16:9 Standard</span>
                  </div>
                </div>

                {/* Recommended Reading */}
                {recentBlogs.length > 0 && (
                  <div className="qr-sidebar-recs-card">
                    <h4 className="qr-sidebar-heading">{"// RECOMMENDED ARTICLES"}</h4>
                    <div className="qr-sidebar-recs-list">
                      {recentBlogs
                        .filter(b => (b.slug || b._id) !== (blogData.slug || blogData._id))
                        .slice(0, 3)
                        .map((item, idx) => (
                          <div
                            key={item._id || idx}
                            className="qr-sidebar-rec-item"
                            onClick={() => {
                              navigate(`/Blog/${item.slug || item._id}`, { state: { props: item } });
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            <span className="cat">{item.category || "Insights"}</span>
                            <h5 className="title">{item.title}</h5>
                            <span className="date">{formatDate(item.createdAt)}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default FullBlog;
